/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useMemo } from 'react';

import { Button, Tag, Typography2 } from '@coursera/cds-core';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import TrackedButton from 'bundles/page/components/TrackedButton';
import SkillsTag from 'bundles/unified-description-page-common/components/modules/product-overview/SkillsTag';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { useSkillsAndToolsDialog } from 'bundles/unified-description-page-common/contexts/SkillsAndToolsDialogContext';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';

import _t from 'i18n!nls/xdp';

const CHARACTER_LIMIT = {
  DESKTOP: 180,
  MOBILE: 120,
};

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  button: css`
    padding: 0;
    margin-left: 0;
    text-decoration: underline;

    :hover {
      background: transparent;
      text-decoration: none;
    }
  `,
  hideSkill: css`
    display: none;
  `,
  tagOverrides: css`
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-max);
  `,
  seoTagOverrides: css`
    display: block;
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-max) !important;
    background-color: var(--cds-color-neutral-disabled);
    line-height: 20px;

    span {
      padding: 0;
      border-radius: 0;
    }
  `,
};

type Props = {
  skillsTitle?: string;
  toolsTitle?: string;
  skillTags?: ConsumerSpecializationPageFieldsFragment['skillTags'];
  disableClickablePills?: boolean;
};

export const getLastIndex = (skillTags: Props['skillTags'], characterLimit: number) => {
  if (!skillTags?.length) {
    return -1;
  }

  let remainingCharacterCount = characterLimit;

  const index = skillTags.findIndex((skillTag) => {
    remainingCharacterCount -= skillTag.name.length;
    return remainingCharacterCount < 0;
  });

  if (index === -1) {
    return skillTags.length - 1;
  } else if (index === 0) {
    return index;
  }

  return index - 1;
};

const SkillsAndTools = ({ skillTags, skillsTitle, toolsTitle, disableClickablePills }: Props) => {
  const { openDialog, setIsEnableSkillsLinking } = useSkillsAndToolsDialog();
  const { subfolderLocaleCode } = usePageInfo();

  const isMobile = useIsMobile();
  const characterLimit = isMobile ? CHARACTER_LIMIT.MOBILE : CHARACTER_LIMIT.DESKTOP;

  const skills = (skillTags ?? []).filter((skillTag) => !skillTag.isToolSoftware);
  const tools = (skillTags ?? []).filter((skillTag) => skillTag.isToolSoftware);

  const lastSkillsIndexToShow = useMemo(() => {
    return getLastIndex(skills, characterLimit);
  }, [skills, characterLimit]);
  const lastToolsIndexToShow = useMemo(() => {
    return getLastIndex(tools, characterLimit);
  }, [tools, characterLimit]);

  const isSkillsLinkingEnabled = !disableClickablePills && !subfolderLocaleCode;

  useEffect(() => {
    setIsEnableSkillsLinking(isSkillsLinkingEnabled);
  }, [isSkillsLinkingEnabled, setIsEnableSkillsLinking]);

  if (!skillTags?.length) {
    return null;
  }

  return (
    <div>
      {skills.length > 0 && (
        <Subsection
          title={
            <Typography2 component="h2" variant="subtitleLarge">
              {skillsTitle ?? _t("Skills you'll gain")}
            </Typography2>
          }
          spacing={16}
        >
          <ul css={styles.container} data-testid="skills-section">
            {skills.map((skill, index) => {
              return (
                <li key={skill.id} css={index > lastSkillsIndexToShow ? styles.hideSkill : undefined}>
                  <span data-testid={`skill-tag-${skill.id}`}>
                    {isSkillsLinkingEnabled ? (
                      <SkillsTag skill={skill.name} cssOverrides={styles.seoTagOverrides} />
                    ) : (
                      <Tag css={styles.tagOverrides}>{skill.name}</Tag>
                    )}
                  </span>
                </li>
              );
            })}
            {skills.length - 1 > lastSkillsIndexToShow && (
              <li>
                <Button
                  variant="ghostSecondary"
                  edgeAlign="start"
                  size="small"
                  onClick={openDialog}
                  css={styles.button}
                  component={TrackedButton}
                  trackingName="overview_skills_toggle"
                  requireFullyVisible={false}
                  withVisibilityTracking={false}
                  data-testid="show-all-skills"
                >
                  {_t('Show all')}
                </Button>
              </li>
            )}
          </ul>
        </Subsection>
      )}
      {tools.length > 0 && (
        <Subsection
          title={
            <Typography2 component="h2" variant="subtitleLarge">
              {toolsTitle ?? _t("Tools you'll learn")}
            </Typography2>
          }
          spacing={8}
        >
          <ul css={styles.container} data-testid="tools-section">
            {tools.map((tool, index) => {
              return (
                <li key={tool.id} css={index > lastToolsIndexToShow ? styles.hideSkill : undefined}>
                  <span data-testid={`tool-tag-${tool.id}`}>
                    {isSkillsLinkingEnabled ? (
                      <SkillsTag skill={tool.name} cssOverrides={styles.seoTagOverrides} />
                    ) : (
                      <Tag css={styles.tagOverrides}>{tool.name}</Tag>
                    )}
                  </span>
                </li>
              );
            })}
            {tools.length - 1 > lastToolsIndexToShow && (
              <li>
                <Button
                  variant="ghostSecondary"
                  edgeAlign="start"
                  size="small"
                  onClick={openDialog}
                  css={styles.button}
                  component={TrackedButton}
                  trackingName="overview_tools_toggle"
                  requireFullyVisible={false}
                  withVisibilityTracking={false}
                  data-testid="show-all-tools"
                >
                  {_t('Show all')}
                </Button>
              </li>
            )}
          </ul>
        </Subsection>
      )}
    </div>
  );
};

export default SkillsAndTools;
